import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowLongRightIcon, UserCircleIcon, CalendarIcon } from '@heroicons/react/24/outline';
import { blogAPI } from '../../services/api';
import { getImgUrl } from '../../utils/config';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';

const Blogs = () => {
    const [blogPosts, setBlogPosts] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchBlogs();
    }, []);

    const fetchBlogs = async () => {
        try {
            const response = await blogAPI.getAll();
            setBlogPosts(response.data);
        } catch (error) {
            console.error("Error fetching blogs:", error);
        } finally {
            setLoading(false);
        }
    };

    const fadeInUp = {
        hidden: { opacity: 0, y: 40 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } }
    };

    const staggerContainer = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1
            }
        }
    };

    const getMediaDisplay = (post) => {
        let mediaUrl = post.media_url;

        // Check for new media_urls array
        if (post.media_urls && Array.isArray(post.media_urls) && post.media_urls.length > 0) {
            mediaUrl = post.media_urls[0];
        }

        if (!mediaUrl) {
            return (
                <div className="w-full h-full bg-gradient-to-br from-green-100 to-teal-100 flex items-center justify-center">
                    <span className="text-4xl">📝</span>
                </div>
            );
        }

        const mediaPath = getImgUrl(mediaUrl);

        if (post.media_type === 'video') {
            return (
                <div className="w-full h-full bg-black flex items-center justify-center relative">
                    <video src={mediaPath} className="w-full h-full object-cover opacity-80" muted />
                    <div className="absolute inset-0 flex items-center justify-center">
                        <div className="bg-white/30 backdrop-blur-sm p-3 rounded-full">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-8 h-8 text-white">
                                <path fillRule="evenodd" d="M4.5 5.653c0-1.426 1.529-2.33 2.779-1.643l11.54 6.348c1.295.712 1.295 2.573 0 3.285L7.28 19.991c-1.25.687-2.779-.217-2.779-1.643V5.653z" clipRule="evenodd" />
                            </svg>
                        </div>
                    </div>
                </div>
            );
        }

        return (
            <img
                src={mediaPath}
                alt={post.title}
                className="w-full h-full object-cover transform transition-transform duration-700 group-hover:scale-110"
            />
        );
    };

    return (
        <div className="bg-gray-50 min-h-screen font-sans">
            <SEO
                title="Agriculture Blogs - Latest Farming News & Tips"
                description="Read the latest blogs on smart farming, crop protection, soil health, and agricultural innovations. Stay updated with Marvelous Agri Solution."
                keywords="Agriculture Blog, Farming News, Crop Management Tips, Agri Tech News, Sustainable Farming Blog"
            />
            {/* Hero Section */}
            <div className="pt-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
                <h1 className="text-4xl font-bold text-gray-900 mb-8">Latest Blogs</h1>
                {loading ? (
                    <div className="text-center py-20">Loading...</div>
                ) : (
                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, margin: "-100px" }}
                        variants={staggerContainer}
                        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10"
                    >
                        {Array.isArray(blogPosts) && blogPosts.length > 0 ? (
                            blogPosts.map((post) => (
                                <motion.div
                                    key={post.id}
                                    variants={fadeInUp}
                                    className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 group flex flex-col h-full"
                                >
                                    <div className="h-60 overflow-hidden relative">
                                        <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-md px-3 py-1 rounded-full text-xs font-bold text-gray-900 z-10 uppercase tracking-widest shadow-sm">
                                            {post.media_type}
                                        </div>
                                        {getMediaDisplay(post)}
                                    </div>

                                    <div className="p-6 flex flex-col flex-grow">
                                        <div className="flex items-center gap-4 text-xs text-gray-400 mb-4 font-medium uppercase tracking-wider">
                                            <span className="flex items-center gap-1">
                                                <CalendarIcon className="h-4 w-4" />
                                                {new Date(post.createdAt).toLocaleDateString()}
                                            </span>
                                            {/* <span className="flex items-center gap-1"><UserCircleIcon className="h-4 w-4" /> {post.author_id}</span> */}
                                        </div>
                                        <h2 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-primary transition-colors leading-tight line-clamp-2">
                                            {post.title}
                                        </h2>
                                        <p className="text-gray-600 mb-6 font-light leading-relaxed line-clamp-3">
                                            {post.content ? (post.content.substring(0, 150) + '...') : ''}
                                        </p>
                                        <div className="mt-auto pt-6 border-t border-gray-100">
                                            <Link to={`/blogs/${post.id}`}>
                                                <button className="flex items-center gap-2 text-primary font-bold uppercase text-xs tracking-widest hover:gap-3 transition-all">
                                                    Read Article <ArrowLongRightIcon className="h-5 w-5" />
                                                </button>
                                            </Link>
                                        </div>
                                    </div>
                                </motion.div>
                            ))
                        ) : (
                            <div className="col-span-full text-center py-20 text-gray-500">
                                No blog posts found.
                            </div>
                        )}
                    </motion.div>
                )}


            </div>
        </div>
    );
};

export default Blogs;
